// WHAT: Hook for managing reminders with CRUD operations and recurrence calculation
// WHY:  Provides interface for fetching, adding, and marking reminders as done
// DOMAIN: Productivity → Reminders
// DEPENDENCIES: getSupabaseClient from shared services, date-fns for date calculations
// RISKS:
//   - NOTE: Recurrence calculation is client-side - may not match backend logic
//   - NOTE: Error handling is silent - may not provide user feedback
//   - NOTE: Next due date calculation may have timezone issues

import type { Reminder } from "../types";

import { addDays, parseISO, nextDay } from "date-fns";
import { useState, useEffect, useCallback } from "react";

import { getSupabaseClient } from "@/shared/services";

/**
 * WHAT:
 *   Calculates next due date for reminder based on recurrence type.
 *
 * WHY:
 *   Enables automatic scheduling of recurring reminders.
 *
 * BEHAVIOR:
 *   - Handles 'once' recurrence (returns null)
 *   - Handles 'interval' recurrence (adds days)
 *   - Handles 'weekly' recurrence (finds next weekday)
 *   - Uses last_done_at or start_date as base date
 *
 * RISKS:
 *   - NOTE: Recurrence calculation may not match backend logic
 *   - NOTE: Timezone handling may cause issues
 *
 * @param reminder
 *
 * @returns Next due date string or null
 *
 */
function computeNextDue(reminder: Reminder): null | string {
  const base = reminder.last_done_at
    ? parseISO(reminder.last_done_at)
    : parseISO(reminder.start_date);
  switch (reminder.recurrence_type) {
    case "once": {
      return null;
    }
    case "interval": {
      if (reminder.repeat_interval !== null) {
        return addDays(base, reminder.repeat_interval).toISOString().split("T")[0] ?? null;
      }
      return null;
    }
    case "weekly": {
      if (reminder.repeat_weekday !== null) {
        const next = nextDay(base, reminder.repeat_weekday as 0 | 1 | 2 | 3 | 4 | 5 | 6);
        return next.toISOString().split("T")[0] ?? null;
      }
      return null;
    }
    default: {
      return null;
    }
  }
}

/**
 * WHAT:
 *   Hook that provides CRUD operations for reminders with automatic next due
 *   date calculation and Supabase integration.
 *
 * WHY:
 *   Centralizes reminder management logic and provides consistent interface
 *   for reminder operations.
 *
 * BEHAVIOR:
 *   - Fetches reminders on mount
 *   - Provides addReminder function
 *   - Provides markDone function with automatic next due calculation
 *   - Manages local state
 *
 * RISKS:
 *   - NOTE: Recurrence calculation is client-side - may not match backend logic
 *   - NOTE: Error handling is silent - may not provide user feedback
 *   - NOTE: Next due date calculation may have timezone issues
 *
 * @returns Object with reminders, addReminder, markDone, and fetchReminders
 *
 */
export const useReminders = () => {
  const [reminders, setReminders] = useState<Reminder[]>([]);

  const fetchReminders = useCallback(async () => {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("reminders")
      .select("*")
      .order("created_at", { ascending: true });

    if (error) {
      // NOTE: Failed to load reminders
    } else {
      setReminders(data as Reminder[]);
    }
  }, []);

  useEffect(() => {
    void fetchReminders();
  }, [fetchReminders]);

  const addReminder = async (
    reminder: Omit<Reminder, "created_at" | "id" | "is_done" | "last_done_at" | "next_due_date">,
  ) => {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("reminders")
      .insert({ ...reminder, next_due_date: reminder.start_date })
      .select();

    if (error) {
      // NOTE: Failed to add reminder
    } else if (data.length > 0) {
      setReminders((previous) => [...previous, data[0] as Reminder]);
    }
  };

  /**
   * WHAT:
   *   Marks reminder as done and calculates next due date.
   *
   * WHY:
   *   Enables completion tracking and automatic rescheduling of recurring reminders.
   *
   * BEHAVIOR:
   *   - Finds reminder by ID
   *   - Calculates next due date based on recurrence
   *   - Updates reminder in Supabase
   *   - Updates local state
   *
   * @param id
   *
   */
  const markDone = async (id: string) => {
    const reminder = reminders.find((r) => r.id === id);
    if (!reminder) {
      return;
    }

    // NOTE: Calculate next due date based on recurrence type
    const nextDue = computeNextDue(reminder);
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("reminders")
      .update({
        is_done: true,
        last_done_at: new Date().toISOString(),
        next_due_date: nextDue,
      })
      .eq("id", id)
      .select();

    if (error) {
      // NOTE: Failed to mark done
    } else if (data.length > 0) {
      setReminders((previous) => previous.map((r) => (r.id === id ? (data[0] as Reminder) : r)));
    }
  };

  return { addReminder, fetchReminders, markDone, reminders };
};
